import { MiniPackGenerationJobDto } from "../services/miniPackGenerationService";

export const MINI_PACK_COMPLETED_STATUS = 2;

const normalize = (value?: string | null) => (value || "").trim().toLowerCase();

export function isMiniPackJobCompleted(status: number | string | undefined): boolean {
  if (status === MINI_PACK_COMPLETED_STATUS) {
    return true;
  }

  if (typeof status === "string") {
    const normalized = status.trim().toLowerCase();
    return normalized === "completed" || normalized === "2";
  }

  return false;
}

export const getMiniPackStatusLabel = (status: number) => {
  if (status === 0) {
    return "Queued";
  }

  if (status === 1) {
    return "Processing";
  }

  if (status === 2) {
    return "Completed";
  }

  return "Failed";
};

export function jobMatchesAirframeScope(job: MiniPackGenerationJobDto, tailNumber: string): boolean {
  if (!isMiniPackJobCompleted(job.status)) {
    return false;
  }

  const normalizedTail = normalize(tailNumber);
  if (!normalizedTail) {
    return false;
  }

  const scope = normalize(job.scopeLabel);
  if (scope === normalizedTail) {
    return true;
  }

  const sourceScope = normalize(job.sourceScope);
  const jobType = normalize(job.jobType);
  if ((sourceScope === "airframe" || jobType === "airframe") && scope === normalizedTail) {
    return true;
  }

  return false;
}

export function jobMatchesAssemblyScope(
  job: MiniPackGenerationJobDto,
  assemblyLabel: string,
  assemblyFolder: string,
): boolean {
  if (!isMiniPackJobCompleted(job.status)) {
    return false;
  }

  const scope = normalize(job.scopeLabel);
  if (!scope) {
    return false;
  }

  const expectedScope = normalize(`${assemblyLabel} / ${assemblyFolder}`);
  if (expectedScope && scope === expectedScope) {
    return true;
  }

  const folder = normalize(assemblyFolder);
  if (!folder || !scope.includes(folder)) {
    return false;
  }

  const type = normalize(assemblyLabel);
  const jobType = normalize(job.jobType);
  return !type || jobType === type || scope.startsWith(`${type} /`);
}

export function jobMatchesSubAssemblyScope(
  job: MiniPackGenerationJobDto,
  assemblyFolder: string,
): boolean {
  if (!isMiniPackJobCompleted(job.status)) {
    return false;
  }

  const scope = normalize(job.scopeLabel);
  if (!scope) {
    return false;
  }

  const expectedScope = normalize(`SubAssembly / ${assemblyFolder}`);
  if (expectedScope && scope === expectedScope) {
    return true;
  }

  const jobType = normalize(job.jobType);
  const sourceScope = normalize(job.sourceScope);
  return sourceScope === "subassembly"
    && scope.includes(normalize(assemblyFolder))
    && (!jobType || jobType === "subassembly");
}

export function jobMatchesScopeLabelFilter(job: MiniPackGenerationJobDto, scopeLabelFilter: string): boolean {
  const filter = normalize(scopeLabelFilter);
  if (!filter || filter === "all") {
    return true;
  }

  const scope = normalize(job.scopeLabel);
  if (!scope) {
    return false;
  }

  return scope.includes(filter) || filter.includes(scope);
}

export function filterMiniPackJobsBySearch(
  jobs: MiniPackGenerationJobDto[],
  searchQuery: string,
): MiniPackGenerationJobDto[] {
  const query = normalize(searchQuery);
  if (!query) {
    return jobs;
  }

  return jobs.filter((job) => {
    const haystack = [
      job.scopeLabel,
      job.jobType,
      job.fileName,
      getMiniPackStatusLabel(job.status),
      job.errorMessage,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return haystack.includes(query);
  });
}

export const MINI_PACK_ICON_TOOLTIP =
  "Configure mini-pack PDF. Download previous versions from the Mini Pack Export page.";
